<template>
  <v-dialog :value="value" max-width="820px" @input="$emit('input', $event)">
    <v-card>
      <v-card-title>
        <span class="text-h5">Import Devices from JSON</span>
      </v-card-title>
      <v-card-text>
        <p class="text-body-2 mb-3">
          Paste one device object or a list of them, or load a <code>.json</code> file. Devices are matched by
          <code>slug</code>. Nothing is written if any row is invalid.
        </p>

        <div class="d-flex align-center flex-wrap mb-2" style="gap: 8px">
          <v-btn small outlined @click="insertExample">
            <v-icon left small>mdi-code-json</v-icon> Insert example
          </v-btn>
          <v-btn small outlined @click="$refs.file.click()">
            <v-icon left small>mdi-file-upload-outline</v-icon> Load file
          </v-btn>
          <input ref="file" type="file" accept=".json,application/json" style="display: none" @change="onFile" />
          <v-spacer></v-spacer>
          <v-checkbox
            v-model="updateExisting"
            label="Update devices that already exist"
            dense hide-details class="mt-0"
          ></v-checkbox>
        </div>

        <v-textarea
          v-model="text"
          outlined
          rows="14"
          auto-grow
          spellcheck="false"
          class="json-input"
          :error-messages="parseError ? [parseError] : []"
          placeholder='[{ "slug": "jetson-agx-orin-64gb", "name": "Jetson AGX Orin 64GB", ... }]'
          @input="result = null"
        ></v-textarea>

        <!-- Result of a preview or an import -->
        <v-alert v-if="result && result.errors.length" type="error" dense text class="mb-2">
          <div class="font-weight-bold mb-1">Nothing was imported. Fix these rows:</div>
          <div v-for="(e, i) in result.errors" :key="i" class="text-body-2">
            <span v-if="e.index !== null">Row {{ e.index + 1 }}<span v-if="e.slug"> ({{ e.slug }})</span>: </span>{{ e.error }}
          </div>
        </v-alert>
        <v-alert v-else-if="result" :type="imported ? 'success' : 'info'" dense text class="mb-2">
          <div class="font-weight-bold mb-1">{{ imported ? 'Imported' : 'Preview — nothing written yet' }}</div>
          <div class="text-body-2">
            {{ result.added.length }} new<span v-if="result.added.length">: {{ result.added.join(', ') }}</span>
          </div>
          <div class="text-body-2">
            {{ result.updated.length }} updated<span v-if="result.updated.length">: {{ result.updated.join(', ') }}</span>
          </div>
          <div class="text-body-2">
            {{ result.skipped.length }} already exist, skipped<span v-if="result.skipped.length">: {{ result.skipped.join(', ') }}</span>
          </div>
        </v-alert>

        <div class="text-caption grey--text">
          Required: slug, name, series, unified_memory_gb, tops, gpu_arch, memory_bandwidth_gbs.
          Optional: cuda_cores, tensor_cores, power_w, compute_capability, cpu_cores, cpu_freq_ghz, product_url, image_url, preliminary.
        </div>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="grey" text @click="$emit('input', false)">{{ imported ? 'Close' : 'Cancel' }}</v-btn>
        <v-btn outlined color="primary" :loading="busy === 'preview'" :disabled="!text.trim() || !!busy" @click="submit(true)">
          Preview
        </v-btn>
        <v-btn color="primary" :loading="busy === 'import'" :disabled="!text.trim() || !!busy || imported" @click="submit(false)">
          Import
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { apiService } from '../../service/api.service'

const EXAMPLE = [
  {
    slug: 'jetson-agx-orin-64gb',
    name: 'Jetson AGX Orin 64GB',
    series: 'Jetson AGX Orin Series',
    unified_memory_gb: 64,
    tops: 275,
    gpu_arch: 'Ampere',
    memory_bandwidth_gbs: 204.8,
    cuda_cores: 2048,
    tensor_cores: 64,
    power_w: '15W-60W',
    compute_capability: '8.7',
    cpu_cores: 12,
    cpu_freq_ghz: 2.2,
    product_url: 'https://openzeka.com/urun-kategori/gelistirici-kitleri/',
    image_url: 'https://openzeka.com/wp-content/uploads/example.png',
    preliminary: false
  }
]

export default {
  name: 'JetsonImportDialog',
  props: {
    value: { type: Boolean, default: false }
  },
  data () {
    return {
      text: '',
      updateExisting: false,
      parseError: '',
      result: null,
      imported: false,
      busy: null
    }
  },
  watch: {
    value (open) {
      if (open) {
        this.text = ''
        this.parseError = ''
        this.result = null
        this.imported = false
      }
    },
    updateExisting () {
      this.result = null
    }
  },
  methods: {
    insertExample () {
      this.text = JSON.stringify(EXAMPLE, null, 2)
      this.parseError = ''
      this.result = null
    },
    onFile (event) {
      const file = event.target.files && event.target.files[0]
      event.target.value = ''
      if (!file) return
      const reader = new FileReader()
      reader.onload = () => {
        this.text = String(reader.result || '')
        this.parseError = ''
        this.result = null
      }
      reader.readAsText(file)
    },
    parse () {
      try {
        const data = JSON.parse(this.text)
        if (Array.isArray(data) ? !data.length : (typeof data !== 'object' || data === null)) {
          this.parseError = 'Expected a device object or a non-empty list of devices.'
          return null
        }
        this.parseError = ''
        return data
      } catch (e) {
        this.parseError = `Invalid JSON: ${e.message}`
        return null
      }
    },
    async submit (dryRun) {
      const devices = this.parse()
      if (!devices) return
      this.busy = dryRun ? 'preview' : 'import'
      try {
        const { data } = await apiService.post('admin/llm_models/jetson-devices/import', {
          devices,
          update_existing: this.updateExisting,
          dry_run: dryRun
        })
        this.result = data.data
        if (!dryRun) {
          this.imported = true
          const r = data.data
          this.$emit('saved', `Imported: ${r.added.length} new, ${r.updated.length} updated, ${r.skipped.length} skipped`)
        }
      } catch (e) {
        const payload = e.response && e.response.data
        if (payload && payload.data && payload.data.errors) {
          this.result = payload.data
        } else {
          this.$emit('error', (payload && payload.error) || 'Import failed')
        }
      }
      this.busy = null
    }
  }
}
</script>

<style scoped>
.json-input ::v-deep textarea {
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace !important;
  font-size: 12.5px;
  line-height: 1.5;
}
</style>
